import {
  MOBILE_NAV_COMPACT_TITLE_VAR,
  MOBILE_NAV_MATERIAL_VAR,
  MOBILE_NAV_TITLE_PROGRESS_VAR,
  MOBILE_NAV_TITLE_TUCK_VAR,
} from './layoutConstants';

/**
 * WhatsApp-style mobile large-title stages, driven by absolute geometry.
 *
 * Each stage is a clipped remap of the same scroll position — scrubbing back
 * and forth always lands on the correct interpolated value for that range
 * (no direction/velocity state).
 *
 * 1. Content (search / stats) scrolls under the large title — document flow.
 * 2. Large title tucks under the floating header pills (`tuck` 0→1).
 * 3. Compact centered title fades in after tuck is mostly done (`compact`).
 * 4. Progressive frosted material strengthens under the chrome (`material`).
 */

export type MobileNavTitleStages = {
  /** Stage 2: 0 = large title fully below header, 1 = fully tucked under it. */
  tuck: number;
  /** Stage 3: compact nav title opacity (clipped to the end of the tuck range). */
  compact: number;
  /** Stage 4: header frosted material / progressive blur strength. */
  material: number;
};

/** Compact title starts once the large title is mostly under the bar. */
export const MOBILE_NAV_COMPACT_START = 0.7;
/** Material reaches full strength halfway through the tuck. */
export const MOBILE_NAV_MATERIAL_FULL_AT = 0.55;

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

/**
 * Remap `value` from `[start, end]` → `[0, 1]`, clipped outside the range.
 */
export function clipScrollRange(value: number, start: number, end: number): number {
  if (end <= start) return value >= end ? 1 : 0;
  return clamp01((value - start) / (end - start));
}

export function computeMobileNavTitleStages(args: {
  headerBottom: number;
  titleTop: number;
  titleHeight: number;
}): MobileNavTitleStages {
  const height = Math.max(args.titleHeight, 1);
  // Stage 2 — title top crossing header bottom → title fully above that line.
  const tuck = clipScrollRange(args.headerBottom - args.titleTop, 0, height);
  const compact = clipScrollRange(tuck, MOBILE_NAV_COMPACT_START, 1);
  const material = clipScrollRange(tuck, 0, MOBILE_NAV_MATERIAL_FULL_AT);
  return { tuck, compact, material };
}

const TITLE_STAGE_VARS = [
  MOBILE_NAV_TITLE_TUCK_VAR,
  MOBILE_NAV_COMPACT_TITLE_VAR,
  MOBILE_NAV_TITLE_PROGRESS_VAR,
] as const;

export function writeMobileNavTitleStages(
  root: HTMLElement,
  stages: MobileNavTitleStages,
  opts?: { material?: boolean },
) {
  root.style.setProperty(MOBILE_NAV_TITLE_TUCK_VAR, String(stages.tuck));
  root.style.setProperty(MOBILE_NAV_COMPACT_TITLE_VAR, String(stages.compact));
  root.style.setProperty(MOBILE_NAV_TITLE_PROGRESS_VAR, String(stages.compact));
  if (opts?.material !== false) {
    root.style.setProperty(MOBILE_NAV_MATERIAL_VAR, String(stages.material));
  }
}

export function clearMobileNavTitleStages(root: HTMLElement, opts?: { material?: boolean }) {
  for (const name of TITLE_STAGE_VARS) root.style.removeProperty(name);
  if (opts?.material !== false) root.style.removeProperty(MOBILE_NAV_MATERIAL_VAR);
}

/** In-page large title: reverse of the compact nav title on the same stage. */
export const MOBILE_LARGE_TITLE_STAGE_CSS = {
  '@media screen and (max-width: 47.99em)': {
    opacity: `calc(1 - var(${MOBILE_NAV_COMPACT_TITLE_VAR}, 0))`,
    transformOrigin: 'center center',
    transform: `translateY(calc(var(${MOBILE_NAV_COMPACT_TITLE_VAR}, 0) * -6px))`,
    filter: `blur(calc(var(${MOBILE_NAV_COMPACT_TITLE_VAR}, 0) * 8px))`,
    willChange: 'opacity, transform, filter',
  },
} as const;
